/**
 * Translation loader with static imports - all languages bundled together
 *
 * This module bundles all languages into a single chunk.
 * Benefits:
 * - All languages immediately available (no loading delay when switching)
 * - Single bundle = better gzip/brotli compression
 * - Fewer HTTP requests (1 instead of 5)
 * - Faster language switching (no network roundtrip)
 * - Perfect for expense sharing apps where users view multilingual content
 */
import { type ActuallySupportedLanguage, FALLBACK_LANGUAGE } from './config';
import deTranslations from './locales/de/index';
import enTranslations from './locales/en/index';
import esTranslations from './locales/es/index';
import frTranslations from './locales/fr/index';
import ptTranslations from './locales/pt/index';

// Type for translation data structure: { namespace: { key: value } }
type LanguageTranslations = Record<string, Record<string, any>>;

// Static map of all language bundles (loaded immediately)
const languageBundles = new Map<string, LanguageTranslations>([
  ['en', enTranslations],
  ['es', esTranslations],
  ['fr', frTranslations],
  ['de', deTranslations],
  ['pt', ptTranslations],
]);

/**
 * Get language bundle (synchronous - all languages already loaded)
 */
function getLanguageBundle(
  language: ActuallySupportedLanguage
): LanguageTranslations {
  const bundle = languageBundles.get(language);
  if (!bundle) {
    return {};
  }
  return bundle;
}

/**
 * Load a specific namespace for a language
 * Now synchronous since all languages are pre-loaded
 */
export async function loadTranslation(
  language: string,
  namespace: string
): Promise<Record<string, any>> {
  try {
    const bundle = getLanguageBundle(language as ActuallySupportedLanguage);
    return bundle[namespace] || {};
  } catch {
    return {};
  }
}

/**
 * Resolve a dotted key path (e.g. `expense.created.title`) within a
 * namespace object, returning the string leaf or undefined when any segment
 * is missing or the leaf isn't a string.
 */
function resolveKeyPath(
  source: Record<string, any>,
  keyPath: string
): string | undefined {
  let value: any = source;
  for (const part of keyPath.split('.')) {
    if (value && typeof value === 'object' && part in value) {
      value = value[part];
    } else {
      return undefined;
    }
  }
  return typeof value === 'string' ? value : undefined;
}

/**
 * Translate a fully-qualified `namespace.key.path` (e.g.
 * `notifications.expense.created.title`) into the given language, with
 * `{{param}}` interpolation. Synchronous and React-free, so it's safe to use
 * server-side (Node).
 *
 * This mirrors how the client resolves and interpolates the same keys
 * (`SimpleI18n`): split the namespace off the first segment, walk the dotted
 * path, then replace `{{name}}` placeholders. It exists so server-rendered
 * text — e.g. push-notification alerts, which the OS renders from the literal
 * strings we send and therefore can't translate from keys on the client —
 * matches the in-app experience.
 *
 * Falls back to {@link FALLBACK_LANGUAGE} when the key is missing in the
 * requested language, and returns the key itself if still unresolved.
 *
 * @param language - Target language code (falls back to English).
 * @param key - Fully-qualified key: `namespace` + `.` + dotted key path.
 * @param params - Interpolation values for `{{name}}` placeholders.
 */
export function translate(
  language: string,
  key: string,
  params?: Record<string, unknown>
): string {
  const firstDot = key.indexOf('.');
  if (firstDot === -1) {
    return key;
  }
  const namespace = key.slice(0, firstDot);
  const keyPath = key.slice(firstDot + 1);

  const lookup = (lang: string): string | undefined => {
    const ns = getLanguageBundle(lang as ActuallySupportedLanguage)[namespace];
    return ns ? resolveKeyPath(ns, keyPath) : undefined;
  };

  let value = lookup(language);
  if (value === undefined && language !== FALLBACK_LANGUAGE) {
    value = lookup(FALLBACK_LANGUAGE);
  }
  if (value === undefined) {
    return key;
  }

  if (params) {
    return value.replace(/{{(\w+)}}/g, (match, name) =>
      params[name] !== undefined ? String(params[name]) : match
    );
  }
  return value;
}

/**
 * Preload entire language bundle for better initial load performance
 * No-op now since all languages are statically loaded
 * @deprecated All languages are now pre-loaded, no need to call this
 */
export function preloadCriticalTranslations(_language: string): void {
  // No-op - all languages already loaded
}

/**
 * Clear all cached translations
 * No-op now since translations are static
 * @deprecated Translations are now static, cannot be cleared
 */
export function clearTranslationCache(): void {
  // No-op - translations are static
}
